import {Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {BucketName, IncidentBasedOn, IncidentResponse, IncidentType, ReferenceBookResponse, ReferenceBookType} from '@npck/af-frontend-api';
import {Spinner} from '@npck/libs/commons';
import {combineLatest} from 'rxjs';
import {DictionarySelectorComponent} from '../../../../components/dictionary/dictionary-selector.component';
import {AttachmentsForm, buildAttachmentsForm} from '../../../../components/files/files.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DESCRIPTION_MAX_LENGTH, DESCRIPTION_MIN_LENGTH} from '../../../../domain/constants';
import {DictionaryHolderService, findValueInDictionary, findValueInDictionaryBy} from '../../../../domain/dictionary-holder.service';

@Component({
    selector: 'af-common-information-step',
    templateUrl: './common-information-step.component.html',
    styleUrl: './common-information-step.component.scss',
    standalone: false
})
export class CommonInformationStepComponent implements OnInit {
    @Input() form: FormGroup<CommonInformationForm>;
    @Input() incident: IncidentResponse;
    @Output() nextStep = new EventEmitter<void>;
    @Output() typeChangeListener = new EventEmitter<string>;
    @Output() cancel = new EventEmitter<void>;
    @ViewChild('governmentOrganizationsSelector') governmentOrganizationsSelector!: DictionarySelectorComponent;
    Spinner = Spinner;
    DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;
    DESCRIPTION_MIN_LENGTH = DESCRIPTION_MIN_LENGTH;
    organizations: ReferenceBookResponse[];
    BucketName = BucketName;
    hasFullAccessRoles: boolean;
    hasOtherAccessRoles: boolean;
    excludeIncidentTypes: string[];
    excludeBasedOn: string[];
    private destroyRef = inject(DestroyRef);

    constructor(
        public authHolderService: AuthHolderService,
        private formBuilder: FormBuilder,
        private dictionaryHolderService: DictionaryHolderService,
    ) {
    }

    get isTypeDisabled(): boolean {
        return !!this.incident && !this.authHolderService.hasAccess(['npck-admin']);
    }

    get isFraudTypeDisabled(): boolean {
        return !!this.incident
            && this.incident.typeCode === IncidentType.OTHER
            && !this.authHolderService.hasAccess(['npck-admin']);
    }

    get isGovernmentOrganizationsRequired(): boolean {
        return this.form.controls.governmentOrganizations?.hasValidator(Validators.required) ?? false;
    }

    submit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        this.nextStep.emit();
    }

    ngOnInit(): void {
        this.hasFullAccessRoles = this.authHolderService.hasAccess(['npck-admin', 'government-user']);
        this.hasOtherAccessRoles = this.authHolderService.hasAccess(['npck-admin', 'finance-user']);
        const hasMsisdnFraudAccess = this.authHolderService.hasAccess(['npck-admin', 'mobile-operator-user']);
        this.excludeIncidentTypes = [
            ...(!this.hasFullAccessRoles ? [IncidentType.COMPROMISED_PAYMENT_INSTRUMENT] : []),
            ...(!this.hasOtherAccessRoles ? [IncidentType.OTHER] : []),
            ...(!hasMsisdnFraudAccess ? [IncidentType.MSISDN_FRAUD] : []),
        ].filter(type => type !== this.incident?.typeCode);
        this.excludeBasedOn = [...(this.authHolderService.hasAccess(['government-user']) ? [IncidentBasedOn.SD, IncidentBasedOn.AC] : [])];
        if (this.form.controls.type) {
            this.subscribeToTypeChanges();
            return;
        }
        if (this.incident) {
            combineLatest(
                [
                    ReferenceBookType.INCIDENT_TYPE,
                    ReferenceBookType.FRAUD_TYPE,
                    ReferenceBookType.BASED_ON,
                    ReferenceBookType.BIN,
                    ReferenceBookType.ATTACHED_DOC,
                ].map(value => this.dictionaryHolderService.getReference(value.toLowerCase())))
                .subscribe(
                    ([
                         incidentTypeDictionary,
                         fraudTypeDictionary,
                         basedOnDictionary,
                         binDictionary,
                         attachedDocDictionary,
                     ],
                    ) => {
                        this.buildForm(<CommonInformationViewModel>{
                            ...this.incident,
                            type: findValueInDictionary(incidentTypeDictionary, this.incident.type),
                            fraud_type: findValueInDictionary(fraudTypeDictionary, this.incident.fraud_type),
                            based_on: findValueInDictionary(basedOnDictionary, this.incident.based_on),
                            governmentOrganization: this.incident.governmentBin
                                ? findValueInDictionaryBy(binDictionary, ['IO'], this.incident.governmentBin)
                                : this.incident.governmentBin,
                            relatedOrganizations:
                                findValueInDictionaryBy(binDictionary, ['BANK', 'PAYM', 'MFO', 'MOBILE_OPERATOR'], this.incident.related),
                        }, attachedDocDictionary);
                    });
        } else {
            if (!this.authHolderService.currentOrgInfo) {
                this.dictionaryHolderService.getReference('bin').subscribe(response => {
                    this.authHolderService.setCurrentOrganization(response.find(item =>
                        item.code === this.authHolderService.organizationBin));
                    this.buildForm();
                });
            } else {
                this.buildForm();
            }
        }
    }

    private subscribeToTypeChanges(): void {
        this.form.controls.type.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                this.typeChangeListener.next(value);
                if (value === IncidentType.MSISDN_FRAUD) {
                    this.form.controls.governmentOrganizations.clearValidators();
                    this.form.controls.basedOn.setValue('SD');
                } else {
                    this.form.controls.governmentOrganizations.setValidators([Validators.required]);
                }
                this.syncGovernmentOrganization(value);
                this.form.controls.governmentOrganizations.updateValueAndValidity();
            });
    }

    private syncGovernmentOrganization(incidentTypeValue: string): void {
        if (!this.incident) {
            const userHasGovernmentOrganization = incidentTypeValue !== IncidentType.MSISDN_FRAUD
                && (this.form.controls.governmentOrganizations?.hasValidator(Validators.required) ?? false)
                && this.authHolderService.currentOrgInfo?.code1 === 'IO';

            if (userHasGovernmentOrganization) {
                this.form.controls.governmentOrganizations.setValue(this.authHolderService.organizationBin);
                this.governmentOrganizationsSelector.selectedItems = [this.authHolderService.currentOrgInfo];
                this.governmentOrganizationsSelector.updateDisplayedValue();
                this.form.controls.governmentOrganizations.disable();
            } else {
                this.form.controls.governmentOrganizations.setValue(null);
                this.governmentOrganizationsSelector.selectedItems = [];
                this.governmentOrganizationsSelector.updateDisplayedValue();
                this.form.controls.governmentOrganizations.enable();
            }
        }
    }

    private buildForm(viewModel?: CommonInformationViewModel, attachedDoc?: ReferenceBookResponse[]): void {
        if (this.form.controls.type) {
            return;
        }
        this.form.addControl('type', new FormControl(null, [Validators.required]));
        this.form.addControl('fraudType', new FormControl(viewModel?.fraud_type, [Validators.required]));
        this.form.addControl('basedOn', new FormControl(viewModel?.based_on, [Validators.required]));
        this.form.addControl('relatedOrganizations', new FormControl(viewModel?.relatedOrganizations));
        this.form.addControl('files', buildAttachmentsForm(this.formBuilder, viewModel?.attachments, attachedDoc));
        this.form.addControl('description',
            new FormControl(viewModel?.description,
                [Validators.required, Validators.minLength(DESCRIPTION_MIN_LENGTH), Validators.maxLength(DESCRIPTION_MAX_LENGTH)]));
        this.form.addControl('governmentOrganizations', new FormControl(viewModel?.governmentOrganization, [Validators.required]));
        this.subscribeToTypeChanges();
        this.form.controls.type.setValue(viewModel?.type);
        if (this.isTypeDisabled) {
            this.form.controls.type.disable();
        }
        setTimeout(() => {
            this.syncGovernmentOrganization(null);
        }, 0);
    }
}

interface CommonInformationViewModel extends IncidentResponse {
    governmentOrganization: string;
    relatedOrganizations: string[];
}

export interface CommonInformationForm {
    type?: FormControl<string>;
    fraudType?: FormControl<string>;
    basedOn?: FormControl<string>;
    governmentOrganizations?: FormControl<string>;
    relatedOrganizations?: FormControl<string[]>;
    description?: FormControl<string>;
    files?: FormGroup<AttachmentsForm>;
}
